// First impression of the game: a ceremonial kakejiku scroll the
// player inscribes themselves into. NOT a form — an induction.
//
// Visual layout is shared with the multiplayer InscribePanel
// (MultiTwinScrolls.tsx) — same wide-scroll painted backdrop, same
// hero grid + name/team fields + Back/Begin actions. The two screens
// differ only in where Back navigates: solo PCS strips the query
// string to land on the title; multi InscribePanel closes the
// inscribe overlay back to the host/join twin-scrolls. Sharing the
// markup means responsive sizing (desktop / mobile / Fold) all flows
// through one .mts-inscribe rule set instead of two parallel ports.
//
// Server contract: POST /api/player/create returns { player } and
// sets the signed player_id cookie. On 200 we run the stamping
// vermillion-ink-wipe before handing off to SoloPlayScreen.

import { useState, type FormEvent, type ReactElement } from 'react'
import { GLYPHS, Petal } from '../design-system'
import {
  type PlayerIcon,
  type PlayerProfile,
} from '@origami/shared'
import { createPlayer } from '../game/playerApi'
import { RotatePrompt, useIsFoldOpen, useIsMobile } from './useIsMobile'
import { playSfx } from '../sound'
import './PlayerCreationScreen.css'
// .mts-inscribe* styles are shared with the multiplayer InscribePanel
// (see MultiTwinScrolls.css). CSS imports are deduped by Vite so the
// dual import is free; it just ensures the styles are present when
// the user lands on /?solo without ever visiting /?multi.
import './MultiTwinScrolls.css'

interface PlayerCreationScreenProps {
  /** Fired once the server has minted the player + set the cookie. */
  onCreated: (player: PlayerProfile) => void
  /** Optional pre-fill — used by multiplayer entry to surface the
   *  existing identity (carried over from a prior solo run) so the
   *  player can confirm or change it for this match. */
  prefill?: { display_name?: string; team_name?: string; icon?: PlayerIcon }
  /** Override the headline copy — multiplayer entry uses
   *  "Confirm your scroll" instead of "Inscribe your scroll". */
  heading?: string
  subheading?: string
}

// Mascots offered to new players. PLAYER_ICON_KEYS in shared lists
// every valid icon string (kept wide so existing DB rows referencing
// older glyphs still validate); this UI-side list filters down to the
// modern bitmap-art mascots (fox/pup/owl/koi/wolf/phoenix/mantis/
// mouse). SVG-silhouette glyphs (crane/frog/tiger/beetle) are the
// older art approach and are intentionally omitted from new-player
// pick-list — they live on only for legacy player rows.
const MASCOT_PICKS = [
  'fox',
  'pup',
  'owl',
  'koi',
  'wolf',
  'phoenix',
  'mantis',
  'mouse',
] as const satisfies readonly PlayerIcon[]

const ICON_HINTS: Record<(typeof MASCOT_PICKS)[number], string> = {
  fox: 'the cunning',
  pup: 'the loyal',
  owl: 'the watcher',
  koi: 'the steady stream',
  wolf: 'the pack',
  phoenix: 'the reborn',
  mantis: 'the still blade',
  mouse: 'the small spark',
}

// Mascot accent color — matches each animal's `--mascot-deep` value
// in PlayerCreationScreen.css. Used to tint the "chosen" caption
// beneath the picker so the whole pick reads as one identity.
const MASCOT_COLORS: Record<(typeof MASCOT_PICKS)[number], string> = {
  fox: '#c46a1f',
  pup: '#8a5a2c',
  owl: '#4b5a96',
  koi: '#5092b8',
  wolf: '#6f6855',
  phoenix: '#c1311e',
  mantis: '#4a9956',
  mouse: '#b8923a',
}

export function PlayerCreationScreen({
  onCreated,
  prefill,
  heading,
  subheading,
}: PlayerCreationScreenProps): ReactElement {
  const [displayName, setDisplayName] = useState(prefill?.display_name ?? '')
  const [teamName, setTeamName] = useState(prefill?.team_name ?? '')
  const prefillIcon = prefill?.icon
  const initialIcon =
    prefillIcon && (MASCOT_PICKS as readonly string[]).includes(prefillIcon)
      ? (prefillIcon as PlayerIcon)
      : null
  const [icon, setIcon] = useState<PlayerIcon | null>(initialIcon)
  const [submitting, setSubmitting] = useState(false)
  const [stamping, setStamping] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const foldOpen = useIsFoldOpen()
  const isMobile = useIsMobile()
  const compact = foldOpen || isMobile

  const dn = displayName.trim()
  const tn = teamName.trim()
  const ready = dn.length > 0 && dn.length <= 24 && tn.length > 0 && tn.length <= 24 && icon !== null

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!ready || submitting || stamping || !icon) return
    setSubmitting(true)
    setError(null)
    const res = await createPlayer({ display_name: dn, team_name: tn, icon })
    setSubmitting(false)
    if (!res.ok) {
      setError(res.message ?? res.code)
      return
    }
    // Stamp animation: 500ms full-screen vermillion wipe before
    // handing off so the transition feels intentional rather than
    // route-jank. The inscribe sound (brush + hanko) plays under it.
    setStamping(true)
    playSfx('inscribe')
    setTimeout(() => onCreated(res.value.player), 540)
  }

  const onBack = () => {
    window.history.pushState(null, '', '?')
    window.dispatchEvent(new Event('url-changed'))
  }

  const headingText = heading ?? 'Inscribe Yourself'
  const subheadingText =
    subheading ?? 'Choose a symbol to represent your clan.'

  return (
    <div className="paper pcs-page">
      {(isMobile || foldOpen) && <RotatePrompt />}
      {/* Cherry-blossom petal flurry — all pink (`#ffb3c1` is the
          Petal default). Varied sizes, positions, and animation
          delays so the page reads as a soft snowfall of sakura
          drifting behind the scroll. Mounted as siblings of the
          scroll so they float past it without affecting layout. */}
      <div className="petal pcs-petal pcs-petal--a" aria-hidden="true">
        <Petal size={22} />
      </div>
      <div className="petal pcs-petal pcs-petal--b" aria-hidden="true">
        <Petal size={18} />
      </div>
      <div className="petal pcs-petal pcs-petal--c" aria-hidden="true">
        <Petal size={20} />
      </div>
      <div className="petal pcs-petal pcs-petal--d" aria-hidden="true">
        <Petal size={16} />
      </div>
      <div className="petal pcs-petal pcs-petal--e" aria-hidden="true">
        <Petal size={24} />
      </div>
      <div className="petal pcs-petal pcs-petal--f" aria-hidden="true">
        <Petal size={14} />
      </div>
      <div className="petal pcs-petal pcs-petal--g" aria-hidden="true">
        <Petal size={19} />
      </div>
      <div className="petal pcs-petal pcs-petal--h" aria-hidden="true">
        <Petal size={17} />
      </div>
      <div className="petal pcs-petal pcs-petal--i" aria-hidden="true">
        <Petal size={21} />
      </div>
      <div className="petal pcs-petal pcs-petal--j" aria-hidden="true">
        <Petal size={15} />
      </div>
      <div className="petal pcs-petal pcs-petal--k" aria-hidden="true">
        <Petal size={18} />
      </div>
      <div className="petal pcs-petal pcs-petal--l" aria-hidden="true">
        <Petal size={23} />
      </div>
      <div className="petal pcs-petal pcs-petal--m" aria-hidden="true">
        <Petal size={13} />
      </div>
      <div className="petal pcs-petal pcs-petal--n" aria-hidden="true">
        <Petal size={20} />
      </div>
      <div className="petal pcs-petal pcs-petal--o" aria-hidden="true">
        <Petal size={16} />
      </div>
      <div className="petal pcs-petal pcs-petal--p" aria-hidden="true">
        <Petal size={22} />
      </div>

      <form
        className={`mts-inscribe ${compact ? 'mts-inscribe--compact' : ''}${foldOpen ? ' mts-inscribe--fold' : ''}`}
        onSubmit={onSubmit}
        noValidate
      >
        <div className="mts-inscribe__scroll">
          <div className="mts-inscribe__body">
            <header className="mts-inscribe__header">
              <h2 className="mts-inscribe__title">{headingText}</h2>
              <p className="mts-inscribe__sub">{subheadingText}</p>
            </header>

            <fieldset className="pcs-mascots">
              <legend className="hand-note pcs-mascots__legend">
                <span className="pcs-mascots__legend-kanji" aria-hidden>家紋</span>
                <span>pick a mascot</span>
              </legend>
              <div
                className="pcs-mascots__grid"
                role="radiogroup"
                aria-label="player mascot"
              >
                {MASCOT_PICKS.map((key) => {
                  const Glyph = GLYPHS[key]
                  if (!Glyph) return null
                  const selected = icon === key
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-icon={key}
                      className={`pcs-mascot${selected ? ' pcs-mascot--selected' : ''}`}
                      onClick={() => setIcon(key)}
                      title={ICON_HINTS[key]}
                    >
                      <span className="pcs-mascot__circle">
                        <Glyph size={compact ? 32 : 44} />
                      </span>
                      <span className="pcs-mascot__seal" aria-hidden="true">朱</span>
                    </button>
                  )
                })}
              </div>
              <p className="hand-note pcs-mascots__hint" aria-live="polite">
                {icon && MASCOT_PICKS.includes(icon as (typeof MASCOT_PICKS)[number]) ? (
                  <>
                    <span
                      className="pcs-mascots__chosen"
                      style={{
                        color: MASCOT_COLORS[icon as (typeof MASCOT_PICKS)[number]],
                      }}
                    >
                      {icon.replace('_', ' ')}
                    </span>
                    <span aria-hidden> · </span>
                    <span>{ICON_HINTS[icon as (typeof MASCOT_PICKS)[number]]}</span>
                  </>
                ) : (
                  <span aria-hidden>&nbsp;</span>
                )}
              </p>
            </fieldset>

            <div className="pcs-right-col">
              <div className="mts-inscribe__fields">
                <Inscription
                  label="Your name in ink"
                  value={displayName}
                  onChange={setDisplayName}
                  placeholder="Hokusai"
                  maxLength={24}
                />
                <Inscription
                  label="Your team"
                  value={teamName}
                  onChange={setTeamName}
                  placeholder="Wave Crashers"
                  maxLength={24}
                />
              </div>

              {error && (
                <div className="pcs-error" role="alert">
                  <span className="pcs-error__dot" aria-hidden="true" />
                  {error}
                </div>
              )}

              <div className="pcs-stamp-row">
                <button
                  type="submit"
                  className={`pcs-stamp${ready ? '' : ' pcs-stamp--disabled'}`}
                  disabled={!ready || submitting || stamping}
                >
                  <span className="pcs-stamp__inner">
                    <span className="pcs-stamp__kanji">朱</span>
                    <span className="pcs-stamp__label">begin</span>
                  </span>
                </button>
                <p className="pcs-stamp-note tiny-cap">
                  press the stamp to begin
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      <button
        type="button"
        className="pcs-back"
        onClick={onBack}
        aria-label="back to title"
      >
        ‹ Back
      </button>

      {/* full-screen vermillion ink wipe on submit */}
      {stamping && <div className="pcs-stampwipe" aria-hidden="true" />}
    </div>
  )
}

/** A single washi paper strip with a brush underline and a `n/24`
 *  character counter — same Inscription primitive used in the
 *  original 2-column PCS layout. */
function Inscription(props: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  maxLength?: number
}): ReactElement {
  const { label, value, onChange, placeholder, maxLength = 24 } = props
  return (
    <label className="pcs-field">
      <span className="pcs-field__label tiny-cap">{label}</span>
      <span className="pcs-field__strip">
        <input
          className="pcs-field__input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete="off"
          spellCheck={false}
        />
        <span className="pcs-field__underline" aria-hidden="true" />
      </span>
      <span className="pcs-field__count tiny-cap">
        {value.trim().length}/{maxLength}
      </span>
    </label>
  )
}
